/**
 * A stable React Query / memo key for a SET of codes, and the way back to the list.
 *
 * ---- The bug this exists to make impossible ----
 *
 * Four hooks each did the same thing by hand:
 *
 *     const key = [...new Set(codes)].sort().join(',')
 *     const unique = useMemo(() => (key ? key.split(',') : []), [key])
 *
 * A comma joins the set for a cheap stable key, and the same comma splits it back. That is
 * correct only while no VALUE contains a comma — and real ones do:
 *
 *     item     "NAPKIN PAPER, WHITE 30*30-70 SHEETS1*40"
 *     supplier "SEA LAND FOODSTUFF TRADG LLC,AJMAN"
 *
 * The napkin code came back as `["NAPKIN PAPER", " WHITE 30*30-70 SHEETS1*40"]` — two codes the
 * catalogue has never heard of. The item resolved to nothing, and the row reported "not in
 * catalogue" about an item that IS in the catalogue. Nothing errored: the code was destroyed
 * before the request was ever made.
 *
 * A NUL is the separator instead. It cannot appear in a Frappe docname, so nothing it joins can
 * be shredded by splitting it again.
 */

/** Separator that cannot occur inside a docname — see the header. */
const SEP = '\u0000'

/**
 * `codes -> one string`, de-duplicated and (by default) sorted so the same SET always produces
 * the same key regardless of the order it arrived in.
 *
 * `sort: false` keeps the caller's order, which matters where the list is walked in a deliberate
 * sequence — `useSupplierEmailsByCode` resolves the purchaser's current selection first.
 */
export function codeKey(codes: readonly string[], options?: { sort: boolean }): string {
  const unique = [...new Set(codes.filter((c) => c !== ''))]
  if (options?.sort !== false) unique.sort()
  return unique.join(SEP)
}

/**
 * `key -> codes`. An empty key gives an EMPTY list, never `['']` — a blank code would be sent to
 * the server, come back null, and look exactly like an item that does not exist.
 */
export function codesFromKey(key: string): string[] {
  return key === '' ? [] : key.split(SEP)
}
